import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, WS_BASE } from "@/lib/api";
import { fmtTime } from "@/lib/time";
import { Car, CheckCircle2, Clock, Star, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const publicApi = axios.create({ baseURL: API });

export default function GuestView() {
  const { token } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  const [needsVerification, setNeedsVerification] = useState(false);
  const [plateLast4, setPlateLast4] = useState("");
  const [verifyError, setVerifyError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [lockedOut, setLockedOut] = useState(false);

  // Local state only for UX (button loading, show time picker), NOT for navigation
  const [requesting, setRequesting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [scheduleMinutes, setScheduleMinutes] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [showRetrievalOptions, setShowRetrievalOptions] = useState(false);
  const [selfPickupOtp, setSelfPickupOtp] = useState(null);
  const [requestingSelfPickup, setRequestingSelfPickup] = useState(false);
  const [rated, setRated] = useState(false);
  const [platformStars, setPlatformStars] = useState(0);
  const [hoverPlatform, setHoverPlatform] = useState(0);
  const [issues, setIssues] = useState({
    extra_money_asked: false,
    misbehaved: false,
    late_arrival: false,
    vehicle_damaged: false,
    unauthorized_personal_use: false,
  });
  const [comment, setComment] = useState("");
  const [eta, setEta] = useState(null);
  const [queuePosition, setQueuePosition] = useState(null);
  const [deliveryOtp, setDeliveryOtp] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const [etaAnchor, setEtaAnchor] = useState(null);
  const [displayEtaMinutes, setDisplayEtaMinutes] = useState(null);
  const [etaArrivingSoon, setEtaArrivingSoon] = useState(false);

  useEffect(() => {
    publicApi.get(`/qr/${token}`)
      .then(({ data }) => {
        setCar(data);
        if (data.requires_verification) {
          setNeedsVerification(true);
        } else if (data.retrieval_token) {
          window.history.replaceState(null, '', `/r/${data.retrieval_token}`);
        }
      })
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!car?.id) return;
    let ws, retryCount = 0, retryTimer;
    const connect = () => {
      const retrievalToken = car?.retrieval_token;
      ws = new WebSocket(`${WS_BASE}/ws/car/${car.id}?token=${retrievalToken}`);
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "car_update") { setCar(msg.data); }
      };
      ws.onclose = () => {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
        retryCount++;
        retryTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => ws.close();
    };
    connect();
    const poll = setInterval(async () => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        try {
          const { data } = await publicApi.get(`/retrieval/${car.retrieval_token}`);
          setCar(data);
        } catch { }
      }
    }, 5000);
    return () => {
      clearTimeout(retryTimer);
      clearInterval(poll);
      ws?.close();
    };
  }, [car?.id, car?.retrieval_token, token]);

  useEffect(() => {
    if (!car?.id) return;
    if (
      car?.status === "RETRIEVAL_REQUESTED" ||
      car?.status === "BEING_FETCHED"
    ) {
      if (!eta) fetchEta(car.event_id);
      fetchQueuePosition(car.id);
    } else {
      setQueuePosition(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car?.status]);

  useEffect(() => {
    if (!car?.id) return;
    if (car?.status !== "ARRIVED_AT_GATE") {
      setDeliveryOtp(null);
      setSecondsLeft(null);
      return;
    }
    const fetchOtp = () => {
      publicApi.get(`/retrieval/${car.retrieval_token}/delivery-otp`)
        .then(r => setDeliveryOtp(r.data.otp))
        .catch(err => {
          console.error(
            "DELIVERY OTP ERROR:",
            err.response?.status,
            err.response?.data
          );
          setDeliveryOtp(null);
        });
    };
    const otpTimer = setTimeout(fetchOtp, 500);

    const tick = () => {
      if (!car.gate_timer_expires_at) { setSecondsLeft(null); return; }
      const diff = Math.max(0, Math.floor((new Date(car.gate_timer_expires_at) - new Date()) / 1000));
      setSecondsLeft(diff);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => { clearInterval(interval); clearTimeout(otpTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car?.status, token]);

  useEffect(() => {
    if (car?.status !== "BEING_FETCHED") {
      setEtaAnchor(null);
      setEtaArrivingSoon(false);
      return;
    }
    const rawEta = queuePosition?.estimated_wait_minutes ?? eta;
    if (rawEta == null) return;
    if (!etaAnchor) {
      const cappedMinutes = Math.min(rawEta, 10);
      setEtaAnchor({ startedAt: Date.now(), cappedMinutes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car?.status, queuePosition?.estimated_wait_minutes, eta]);

  useEffect(() => {
    if (!etaAnchor) {
      setDisplayEtaMinutes(null);
      setEtaArrivingSoon(false);
      return;
    }
    const tick = () => {
      const elapsedMinutes = (Date.now() - etaAnchor.startedAt) / 60000;
      const remaining = etaAnchor.cappedMinutes - elapsedMinutes;
      if (remaining <= 1) {
        setDisplayEtaMinutes(1);
        setEtaArrivingSoon(remaining <= 0.15);
      } else {
        setDisplayEtaMinutes(Math.ceil(remaining));
        setEtaArrivingSoon(false);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [etaAnchor]);


  const handleRequestRetrieval = async () => {
    if (!car) return;
    setRequesting(true);
    try {
      const { data } = await publicApi.patch(`/cars/${car.id}/request-retrieval?retrieval_token=${car.retrieval_token}`);
      setCar(data);
    } catch { } finally { setRequesting(false); }
  };

  const handleSelfPickupRequest = async () => {
    if (!car) return;
    setRequestingSelfPickup(true);
    try {
      const { data } = await publicApi.patch(
        `/cars/${car.id}/self-pickup-request?retrieval_token=${car.retrieval_token}`
      );
      setSelfPickupOtp(data.otp);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not request self-pickup. Please try again.");
    } finally {
      setRequestingSelfPickup(false);
    }
  };

  const handleCancelRetrieval = async () => {
    if (!car) return;
    setCancelling(true);
    try {
      const { data } = await publicApi.patch(`/cars/${car.id}/cancel-retrieval?retrieval_token=${car.retrieval_token}`);
      setCar(data);
    } catch (err) {
      alert(err.response?.data?.detail || "Could not cancel retrieval. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const scheduleRetrieval = async (minutes) => {
    setScheduleMinutes(minutes);
    setScheduling(true);
    try {
      const scheduledDate = new Date(Date.now() + minutes * 60000);
      const { data } = await publicApi.patch(
        `/cars/${car.id}/schedule-retrieval?retrieval_token=${car.retrieval_token}`,
        { scheduled_time: scheduledDate.toISOString() }
      );
      setCar(data);
      setShowSchedulePicker(false);
    } catch (err) {
      alert(err.response?.data?.detail || "Could not schedule retrieval");
    } finally {
      setScheduling(false);
    }
  };

  const cancelScheduleRetrieval = async () => {
    try {
      const { data } = await publicApi.patch(
        `/cars/${car.id}/schedule-retrieval/cancel?retrieval_token=${car.retrieval_token}`
      );
      setCar(data);
      setShowSchedulePicker(false);
      setScheduleMinutes(null);
    } catch { alert("Could not cancel scheduled pickup. Please try again."); }
  };

  const fetchEta = async (eventId) => {
    try {
      const { data } = await publicApi.get(`/events/${eventId}/public-stats`);
      setEta(data.avg_retrieval_minutes || 5);
    } catch {
      setEta(5);
    }
  };

  const fetchQueuePosition = async (carId) => {
    try {
      const { data } = await publicApi.get(
        `/cars/${carId}/queue-position`
      );
      setQueuePosition(data);
    } catch { }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!plateLast4 || plateLast4.length < 1) return;
    setVerifying(true);
    setVerifyError(null);

    try {
      const { data } = await publicApi.post(
        `/qr-cards/${token}/verify-retrieval`,
        { plate_last4: plateLast4 }
      );
      setCar(data);
      setNeedsVerification(false);
      if (data.retrieval_token) {
        window.history.replaceState(null, '', `/r/${data.retrieval_token}`);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setLockedOut(true);
      } else {
        setVerifyError(err.response?.data?.detail || "Plate doesn't match. Try again.");
        setPlateLast4("");
      }
    } finally {
      setVerifying(false);
    }
  };


  const rate = async () => {
    try {
      await publicApi.post(`/ratings?retrieval_token=${car.retrieval_token}`, {
        car_id: car.id,
        stars: platformStars,
        issues: issues,
        comment: comment.trim() || null
      });
    } catch { }
    setRated(true);
  };

  if (loading) return (
    <div className="guest-bg flex items-center justify-center px-4">
      <div className="bg-white/10 backdrop-blur-md rounded-3xl p-10 flex flex-col items-center text-white">
        <Loader2 className="w-10 h-10 animate-spin" />
        <p className="mt-4 font-heading">Loading your car…</p>
      </div>
    </div>
  );



  if (invalid || (!car && !needsVerification)) return (
    <div className="guest-bg flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center fade-in-up" data-testid="guest-invalid">
        <div className="w-16 h-16 bg-amber-100 rounded-full mx-auto flex items-center justify-center">
          <Clock className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-[#0F2044] mt-5">Invalid Link</h1>
        <p className="text-gray-500 mt-2 text-sm">Please see the valet attendant.</p>
      </div>
    </div>
  );

  if (needsVerification) return (
    <div className="guest-bg flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center fade-in-up">
        {car?.plate && (
          <div className="mb-4 inline-block bg-gray-100 px-4 py-1.5 rounded-full">
            <span className="font-mono-plate font-bold text-[#0F2044] tracking-wider">{car.plate}</span>
          </div>
        )}
        <h1 className="font-heading text-2xl font-bold text-[#0F2044] mb-2">Verify Your Vehicle</h1>
        <p className="text-gray-500 text-sm mb-6">Enter the last 4 characters of your license plate (or all of it, if it's shorter than 4) to view your car's status.</p>
        
        {lockedOut ? (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm font-semibold border border-red-100">
            Too many attempts — please see the valet attendant.
          </div>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <input 
                type="text" 
                maxLength={4}
                value={plateLast4}
                onChange={(e) => setPlateLast4(e.target.value.toUpperCase())}
                placeholder="e.g. 1234 or 555"
                className="w-full text-center text-3xl tracking-widest font-mono-plate font-bold bg-gray-50 border border-gray-200 rounded-2xl py-4 focus:outline-none focus:ring-2 focus:ring-[#1A3C6E] focus:border-transparent uppercase placeholder-gray-300 placeholder:text-base placeholder:font-normal placeholder:tracking-normal"
              />
            </div>
            {verifyError && (
              <p className="text-red-500 text-sm font-semibold">{verifyError}</p>
            )}
            <button 
              type="submit" 
              disabled={verifying || plateLast4.length < 1}
              className="w-full bg-[#1A3C6E] text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 hover:bg-[#0F2044] transition-colors disabled:opacity-50"
            >
              {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify"}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  const status = car?.status;

  // State Machine: Render the correct screen based on car data
  return (
    <div className="guest-bg">
      <div className="max-w-sm mx-auto px-4 py-8 flex flex-col gap-6 min-h-screen">
        <div className="text-center fade-in-up">
          <div className="inline-flex items-center gap-1.5 bg-white/15 text-white px-3 py-1 rounded-full text-xs backdrop-blur">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
            <span>{car?.event_name}</span>
          </div>
          <h1 className="font-heading text-4xl font-extrabold text-white tracking-tight mt-3">INSTAPARK</h1>
          <div className="mt-3 inline-block bg-white/95 px-4 py-1.5 rounded-full">
            <span className="font-mono-plate font-bold text-[#0F2044] tracking-wider" data-testid="car-plate">{car?.plate}</span>
          </div>
          <p className="text-white/60 text-xs mt-2">{car?.color} {car?.make}</p>
        </div>

        {/* Screen 0: CHECKED_IN */}
        {status === "CHECKED_IN" && (
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center fade-in-up" data-testid="state-checked-in">
            <div className="w-20 h-20 bg-blue-100 rounded-full mx-auto flex items-center justify-center pulse-dot">
              <Car className="w-10 h-10 text-[#1A3C6E]" />
            </div>
            <h2 className="font-heading text-xl font-bold text-[#0F2044] mt-5">🔄 Your car is being parked.</h2>
            <p className="text-gray-500 text-sm mt-1">You can request retrieval once it's fully parked.</p>
            <div className="bouncing-dots mt-5"><span /><span /><span /></div>
          </div>
        )}

        {/* Screen 1 and 2: PARKED (with or without scheduled time) */}
        {status === "PARKED" && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden fade-in-up" data-testid="state-parked">
            {/* Screen 2: Scheduled Confirmation */}
            {car.scheduled_retrieval_time ? (
              <>
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-6 text-center text-white">
                  <div className="w-16 h-16 bg-white/20 rounded-full mx-auto flex items-center justify-center backdrop-blur">
                    <CheckCircle2 className="w-9 h-9 text-white" />
                  </div>
                  <h2 className="font-heading text-xl font-bold mt-4">✅ Retrieval Scheduled</h2>
                  <div className="mt-4 bg-white/20 rounded-xl px-4 py-3 inline-block">
                    <p className="text-white text-lg font-bold">
                      {fmtTime(car.scheduled_retrieval_time)}
                    </p>
                  </div>
                  <p className="text-white/90 text-sm mt-3 max-w-xs mx-auto">
                    Your car will be brought to the exit at this time. Stay near the exit 5 minutes before.
                  </p>
                </div>
                <div className="p-6">
                  <button
                    onClick={cancelScheduleRetrieval}
                    className="w-full rounded-2xl py-3 text-sm font-semibold border border-red-200 text-red-500 hover:bg-red-50 transition-all"
                  >
                    Cancel Schedule
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Screen 1 / 2 header */}
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-6 text-center text-white relative">
                  {showRetrievalOptions && (
                    <button 
                      onClick={() => {
                        setShowRetrievalOptions(false);
                        setShowSchedulePicker(false);
                        setScheduleMinutes(null);
                        setSelfPickupOtp(null);
                      }}
                      className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center bg-white/20 rounded-full backdrop-blur hover:bg-white/30 transition-all"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                    </button>
                  )}
                  <div className="w-16 h-16 bg-white/20 rounded-full mx-auto flex items-center justify-center backdrop-blur">
                    <CheckCircle2 className="w-9 h-9 text-white" />
                  </div>
                  <h2 className="font-heading text-xl font-bold mt-4">Your car is safely parked</h2>
                  {car.zone && <p className="text-white/90 text-sm mt-1">Zone {car.zone} · Slot {car.slot}</p>}
                  {car.no_show_count > 0 && (
                    <p className="text-white/90 text-xs mt-2 bg-white/15 rounded-xl px-3 py-2 inline-block">
                      Your car was sent back to parking because you didn't reach the gate in time. Please request it again below.
                    </p>
                  )}
                </div>
                
                {!showRetrievalOptions ? (
                  <div className="p-6 flex flex-col gap-3">
                    {car?.gps_lat != null && car?.gps_lng != null && (
                      <button
                        onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${car.gps_lat},${car.gps_lng}`, "_blank")}
                        className="w-full rounded-2xl py-3.5 text-sm font-semibold border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all"
                      >
                        📍 See Where My Car Is Parked
                      </button>
                    )}
                    <button
                      onClick={() => setShowRetrievalOptions(true)}
                      className="w-full btn-primary-navy rounded-2xl py-4 text-lg font-semibold"
                    >
                      Retrieve My Car
                    </button>
                  </div>
                ) : (
                  <div className="p-6 flex flex-col gap-3">
                    {car?.can_request_retrieval && (
                      <button
                        onClick={handleRequestRetrieval}
                        disabled={requesting}
                        data-testid="request-car-btn"
                        className="w-full btn-primary-navy rounded-2xl py-4 text-lg font-semibold disabled:opacity-70"
                      >
                        {requesting ? "Requesting…" : "🚗 Send Retrieval Now"}
                      </button>
                    )}
                    <button
                      onClick={() => setShowSchedulePicker(true)}
                      className="w-full rounded-2xl py-3.5 text-sm font-semibold border-2 border-[#1A3C6E] text-[#1A3C6E] hover:bg-[#1A3C6E] hover:text-white transition-all"
                    >
                      ⏰ Schedule Retrieval
                    </button>
                    {showSchedulePicker && (
                      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            How soon are you leaving?
                          </p>
                          <button
                            onClick={() => { setShowSchedulePicker(false); setScheduleMinutes(null); }}
                            className="text-gray-400 hover:text-gray-600 text-lg font-bold"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => scheduleRetrieval(15)}
                            disabled={scheduling}
                            className="rounded-xl py-3.5 text-sm font-bold border-2 border-[#1A3C6E] text-[#1A3C6E] hover:bg-[#1A3C6E] hover:text-white transition disabled:opacity-50"
                          >
                            {scheduling && scheduleMinutes === 15 ? "Scheduling…" : "15 min"}
                          </button>
                          <button
                            onClick={() => scheduleRetrieval(30)}
                            disabled={scheduling}
                            className="rounded-xl py-3.5 text-sm font-bold bg-[#1A3C6E] text-white hover:bg-[#0F2044] transition disabled:opacity-50"
                          >
                            {scheduling && scheduleMinutes === 30 ? "Scheduling…" : "30 min"}
                          </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-center">
                          Your car will be ready when you walk out 🚗
                        </p>
                      </div>
                    )}
                    <button
                      onClick={handleSelfPickupRequest}
                      disabled={requestingSelfPickup}
                      className="w-full rounded-2xl py-3.5 text-sm font-semibold border-2 border-[#1A3C6E] text-[#1A3C6E] hover:bg-[#1A3C6E] hover:text-white transition-all disabled:opacity-70"
                    >
                      🙋 Self Pickup
                    </button>
                    {selfPickupOtp && (
                      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 text-center mt-2">
                        <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Show this code at the valet desk</p>
                        <p className="text-4xl font-extrabold text-[#0F2044] mt-2 tracking-widest">{selfPickupOtp}</p>
                        <p className="text-xs text-gray-400 mt-2">A valet supervisor has been notified you're picking up your own car.</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Screen 3: RETRIEVAL_REQUESTED or BEING_FETCHED */}
        {(status === "RETRIEVAL_REQUESTED" || status === "ACCEPTED" || status === "BEING_FETCHED") && (
          <div
            className="bg-white rounded-3xl shadow-2xl overflow-hidden fade-in-up"
            data-testid="state-retrieval"
          >
            <div className={`p-6 text-center text-white ${status === "BEING_FETCHED"
              ? "bg-gradient-to-br from-[#1A3C6E] to-[#0F2044]"
              : status === "ACCEPTED" 
              ? "bg-gradient-to-br from-[#2563EB] to-[#1E40AF]"
              : "bg-gradient-to-br from-amber-500 to-amber-600"
              }`}>
              <div className="w-16 h-16 bg-white/20 rounded-full mx-auto
                flex items-center justify-center backdrop-blur">
                <Car className="w-9 h-9 text-white" />
              </div>
              <h2 className="font-heading text-xl font-bold mt-4">
                {status === "BEING_FETCHED"
                  ? "Your car is on the way 🚗"
                  : status === "ACCEPTED"
                  ? "A driver has accepted your request"
                  : "Request received!"}
              </h2>
              {status === "RETRIEVAL_REQUESTED" ? (
                <>
                  <p className="text-white/90 text-sm mt-2">✅ Request received! A driver will accept it shortly.</p>
                  {queuePosition && <p className="text-white/80 text-sm mt-1">Queue position: {queuePosition.position}</p>}
                </>
              ) : status === "ACCEPTED" ? (
                <p className="text-white/90 text-sm mt-2">🔑 Getting your keys now!</p>
              ) : (
                <p className="text-white/90 text-sm mt-2">🚗 Your car is being fetched right now!</p>
              )}
            </div>

            <div className="px-6 py-5">
              {status === "BEING_FETCHED" && displayEtaMinutes != null ? (
                <div className="bg-amber-50 border border-amber-100
                  rounded-2xl p-4 text-center mb-4">
                  {etaArrivingSoon ? (
                    <>
                      <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">
                        Almost There
                      </p>
                      <p className="text-2xl font-extrabold text-[#0F2044] mt-1">
                        🚗 Arriving any moment
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-bold text-amber-600
                        uppercase tracking-wider">
                        Estimated Wait Time
                      </p>
                      <p className="text-3xl font-extrabold text-[#0F2044] mt-1">
                        ~{displayEtaMinutes} min
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Time left before your car arrives
                      </p>
                    </>
                  )}
                </div>
              ) : (status === "RETRIEVAL_REQUESTED" || status === "ACCEPTED") ? (
                <div className="bg-amber-50 border border-amber-100
                  rounded-2xl p-4 text-center mb-4">
                  <p className="text-lg font-extrabold text-[#0F2044]">
                    ⏳ Waiting for a driver to accept your request
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    We'll show you a time estimate once a driver accepts
                  </p>
                </div>
              ) : null}

              {queuePosition && queuePosition.total_waiting > 1 && (
                <div className="bg-blue-50 border border-blue-100
                  rounded-2xl p-4 text-center mb-4">
                  <p className="text-xs font-bold text-blue-600
                    uppercase tracking-wider">
                    Queue Position
                  </p>
                  {queuePosition.being_fetched ? (
                    <p className="text-2xl font-extrabold
                      text-[#0F2044] mt-1">
                      Your car is being fetched now 🚗
                    </p>
                  ) : (
                    <>
                      <p className="text-3xl font-extrabold
                        text-[#0F2044] mt-1">
                        #{queuePosition.position}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {queuePosition.position === 1
                          ? "You are next in line"
                          : `${queuePosition.position - 1} car${queuePosition.position - 1 > 1 ? "s" : ""
                          } ahead of you`}
                      </p>
                    </>
                  )}
                </div>
              )}

              {status === "RETRIEVAL_REQUESTED" && (
                <button
                  onClick={handleCancelRetrieval}
                  disabled={cancelling}
                  className="w-full rounded-2xl py-3 text-sm font-semibold border border-red-200 text-red-500 hover:bg-red-50 transition-all mb-4 disabled:opacity-60"
                >
                  {cancelling ? "Cancelling…" : "✕ Cancel Request"}
                </button>
              )}

              <div className="relative mt-2">
                <div className="flex justify-between items-center relative z-10">
                  <div className="flex flex-col items-center w-1/4">
                    <div className="w-10 h-10 rounded-full bg-[#1A3C6E]
                      text-white flex items-center justify-center
                      font-bold text-sm">✓</div>
                    <span className="text-xs font-semibold text-[#0F2044]
                      mt-2 text-center">Requested</span>
                  </div>
                  <div className="flex flex-col items-center w-1/4">
                    <div className={`w-10 h-10 rounded-full flex items-center
                      justify-center font-bold text-sm ${status === "ACCEPTED" || status === "BEING_FETCHED"
                        ? "bg-[#1A3C6E] text-white"
                        : "bg-gray-100 text-gray-400"
                      }`}>
                      {status === "ACCEPTED" || status === "BEING_FETCHED" ? "✓" : "2"}
                    </div>
                    <span className={`text-xs font-semibold mt-2 text-center
                      ${status === "ACCEPTED" || status === "BEING_FETCHED"
                        ? "text-[#0F2044]" : "text-gray-400"}`}>
                      Accepted
                    </span>
                  </div>
                  <div className="flex flex-col items-center w-1/4">
                    <div className={`w-10 h-10 rounded-full flex items-center
                      justify-center font-bold text-sm ${status === "BEING_FETCHED"
                        ? "bg-[#1A3C6E] text-white"
                        : "bg-gray-100 text-gray-400"
                      }`}>
                      {status === "BEING_FETCHED" ? "🚗" : "3"}
                    </div>
                    <span className={`text-xs font-semibold mt-2 text-center
                      ${status === "BEING_FETCHED"
                        ? "text-[#0F2044]" : "text-gray-400"}`}>
                      Picked Up
                    </span>
                  </div>
                  <div className="flex flex-col items-center w-1/4">
                    <div className="w-10 h-10 rounded-full bg-gray-100
                      text-gray-400 flex items-center justify-center
                      font-bold text-sm">4</div>
                    <span className="text-xs font-semibold text-gray-400
                      mt-2 text-center">Delivered</span>
                  </div>
                </div>
                <div className="absolute top-5 left-[12.5%] right-[12.5%]
                  h-1 bg-gray-100 rounded-full">
                  <div className={`h-full bg-[#1A3C6E] rounded-full
                    transition-all duration-700 ${status === "BEING_FETCHED" ? "w-2/3" : status === "ACCEPTED" ? "w-1/3" : "w-0"
                    }`} />
                </div>
              </div>

              <p className="text-center text-xs text-gray-400 mt-5">
                Please make your way to the valet area
              </p>
            </div>
          </div>
        )}

        {/* Sub-state A: OTP not yet verified — show timer + OTP code (existing content, unchanged) */}
        {status === "ARRIVED_AT_GATE" && !car?.otp_verified && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden fade-in-up" data-testid="state-arrived-gate">
            <div className="p-6 text-center text-white bg-gradient-to-br from-[#1A3C6E] to-[#0F2044]">
              <div className="w-16 h-16 bg-white/20 rounded-full mx-auto flex items-center justify-center backdrop-blur">
                <Car className="w-9 h-9 text-white" />
              </div>
              <h2 className="font-heading text-xl font-bold mt-4">Your car is at the gate 🚗</h2>
              <p className="text-white/90 text-sm mt-2">Please head to the gate now to collect it.</p>
            </div>
            <div className="px-6 py-5">
              {secondsLeft !== null && (
                <div className={`rounded-2xl p-4 text-center mb-4 border ${secondsLeft <= 60 ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-100"}`}>
                  <p className={`text-xs font-bold uppercase tracking-wider ${secondsLeft <= 60 ? "text-red-600" : "text-amber-600"}`}>
                    Time remaining
                  </p>
                  <p className={`text-3xl font-extrabold mt-1 ${secondsLeft <= 60 ? "text-red-600" : "text-[#0F2044]"}`}>
                    {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, "0")}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Your car will be sent back to parking if you don't arrive in time.
                  </p>
                </div>
              )}
              {deliveryOtp && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 text-center">
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Share this code with your driver</p>
                  <p className="text-4xl font-extrabold text-[#0F2044] mt-2 tracking-widest">{deliveryOtp}</p>
                  <p className="text-xs text-gray-400 mt-2">The driver needs this code to hand over your car.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sub-state B: OTP verified — driver is taking photo and collecting QR card */}
        {status === "ARRIVED_AT_GATE" && car?.otp_verified && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden fade-in-up">

            {/* Header */}
            <div className="p-6 text-center text-white bg-gradient-to-br from-emerald-500 to-emerald-600">
              <div className="w-16 h-16 bg-white/20 rounded-full mx-auto flex items-center justify-center backdrop-blur mb-3">
                <span className="text-4xl">✅</span>
              </div>
              <h2 className="font-bold text-xl">Code Verified!</h2>
              <p className="text-white/90 text-sm mt-1">Almost done — just a few seconds more</p>
            </div>

            {/* Steps for the guest */}
            <div className="px-6 py-5 space-y-4">

              {/* Step 1 */}
              <div className="flex items-start gap-4 bg-gray-50 rounded-2xl p-4">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-600 font-bold text-sm">1</span>
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">Driver is taking a delivery photo</p>
                  <p className="text-gray-400 text-xs mt-0.5">This documents the handover for your protection</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-4 bg-gray-50 rounded-2xl p-4">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-600 font-bold text-sm">2</span>
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">Hand the QR key tag back to the driver</p>
                  <p className="text-gray-400 text-xs mt-0.5">Please return the key tag card you were given at check-in</p>
                </div>
              </div>

              {/* Pulse indicator */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <p className="text-xs text-gray-400">Completing handover...</p>
              </div>

            </div>
          </div>
        )}

        {/* Screen 3.6: AWAITING_REPARK */}
        {status === "AWAITING_REPARK" && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden fade-in-up" data-testid="state-awaiting-repark">
            <div className="p-6 text-center text-white bg-gradient-to-br from-amber-500 to-amber-600">
              <div className="w-16 h-16 bg-white/20 rounded-full mx-auto flex items-center justify-center backdrop-blur">
                <Clock className="w-9 h-9 text-white animate-pulse" />
              </div>
              <h2 className="font-heading text-xl font-bold mt-4">Your car is being re-parked</h2>
              <p className="text-white/90 text-sm mt-2">Our driver is parking your car again. This will just take a moment.</p>
            </div>
            <div className="px-6 py-5 text-center">
              <p className="text-sm text-gray-400 mb-4">Already at the gate? You don't have to wait for it to be re-parked.</p>
              <button
                onClick={handleRequestRetrieval}
                disabled={requesting}
                className="w-full py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold disabled:opacity-60 transition-colors"
              >
                {requesting ? "Sending..." : "I'm at the gate — bring my car back"}
              </button>
            </div>
          </div>
        )}

        {/* Screen 4: DELIVERED */}
        {status === "DELIVERED" && (
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center mx-auto">
            <div className="w-20 h-20 bg-emerald-100 rounded-full mx-auto flex items-center justify-center mb-4">
              <span className="text-5xl">✅</span>
            </div>
            <h1 className="font-bold text-2xl text-gray-800 mb-2">Car Delivered!</h1>
            <p className="text-gray-500 text-sm mb-6">
              Your {car.color} {car.make} ({car.plate}) has been delivered. Thank you for using our valet service.
            </p>

            {!rated ? (
              <div className="border-t border-gray-100 pt-6 space-y-6">
                <div>
                  <p className="font-bold text-gray-700 mb-1">Platform Experience</p>
                  <p className="text-gray-400 text-xs mb-3">Rate the InstaPark platform</p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setPlatformStars(star)}
                        onMouseEnter={() => setHoverPlatform(star)}
                        onMouseLeave={() => setHoverPlatform(0)}
                        className="w-12 h-12 rounded-full bg-gray-50 hover:bg-amber-50 flex items-center justify-center transition-colors border border-gray-200 hover:border-amber-300"
                      >
                        <Star className={`w-6 h-6 ${star <= (hoverPlatform || platformStars) ? "text-amber-400 fill-amber-400" : "text-gray-300"}`} />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="font-bold text-gray-700 mb-1">Tell us if anything went wrong</p>
                  <div className="flex flex-col gap-3 mt-3">
                    {[
                      { key: 'extra_money_asked', label: 'Did the driver ask for extra money?' },
                      { key: 'misbehaved', label: 'Was the driver rude or misbehaving?' },
                      { key: 'late_arrival', label: 'Did the driver arrive late to retrieve your car?' },
                      { key: 'vehicle_damaged', label: 'Was your vehicle damaged?' },
                      { key: 'unauthorized_personal_use', label: 'Did you notice the driver using your vehicle without permission?' },
                    ].map((q) => (
                      <div key={q.key} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 bg-gray-50 p-3 rounded-xl border border-gray-100">
                        <span className="text-sm text-gray-700">{q.label}</span>
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={() => setIssues(prev => ({ ...prev, [q.key]: true }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors border ${issues[q.key] === true ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setIssues(prev => ({ ...prev, [q.key]: false }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors border ${issues[q.key] === false ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Tell us about your experience (optional)"
                    className="w-full min-h-[100px] p-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-y"
                  />
                </div>

                <button
                  onClick={rate}
                  disabled={!platformStars}
                  className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  Submit Rating
                </button>
              </div>
            ) : (
              <div className="border-t border-gray-100 pt-6">
                <p className="text-emerald-600 font-bold">⭐ Thank you for your rating!</p>
                <p className="text-gray-400 text-xs mt-1">Your feedback helps us improve.</p>
              </div>
            )}
          </div>
        )}

        <div className="text-center text-white/40 text-xs mt-auto pt-6">Powered by InstaPark</div>
      </div>
    </div>
  );
}
